import Vue from 'vue';
import { Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { Section } from '@/courses/models/section.model';
import { COURSE_EVENTS } from '@/content/values/events';
import { SECTION_TYPE_CODES } from '@/core/values/ed-transparency-meeting.values';
import EdTeacherMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-teacher-meeting-transparency/ed-teacher-meeting-transparency.component.vue';
import EdStudentMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-student-meeting-transparency/ed-student-meeting-transparency.component.vue';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';

@Component({
  components: {
    VueFlip: () => import('vue-flip'),
    EdTextViewerOnly: () => import('@/courses/components/ed-text-viewer-only/ed-text-viewer-only.vue'),
    EdImagesViewer: () => import('@/content/pages/course-viewer/components/ed-images-viewer/ed-images-viewer.vue'),
    EdTeacherMeetingTransparency,
    EdStudentMeetingTransparency,
  }
})

export default class EdDeckCardComponent extends Vue {
  @Prop() readonly card: Section;
  @Prop() readonly cardIndex: number;
  @Prop({ default: null }) readonly isFlipped: boolean;
  @Prop({ default: null }) readonly maxContainerWidth: number;
  @Prop({ default: null }) readonly maxContainerHeight: number;
  @Prop({ default: 1 }) readonly scale: number;

  @Prop({default: 0}) documentIndex : number;

  flipped: boolean = false;
  frontImageStyles: any = {};
  backImageStyles: any = {};
  hasWhiteboard = false;
  imageUrlBack = null;
  imageUrl = null;

  get deckCardClasses() {
    return {
      'ed-deck-card--has-whiteboard': this.isPresentationMode
    }
  }

  private pagesLoaded = false;


  get hasBack(){
    if(this.card){
      return this.card.sections && this.card.sections.length > 1 ? true : false;
    }
    return false;
  }

  get front() {
    if (this.card) {
      return this.card.sections[0];
    }

    return {}
  }

  get back() {
    if(this.card) {
      return this.card.sections[1];
    }
    return {}
  }

  get currentCardInfo() {
    return this.$store.state.CourseStore.currentCardInfo;
  }

  get isPresentationTeacherMode() {
    return this.$store.state.CourseStore.isPresentationTeacherMode;
  }

  get isPresentationStudentMode() {
    return this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get isPresentationMode() {
    return this.$store.state.CourseStore.isPresentationTeacherMode || this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get pageSizeInfo() : any {
    return this.$store.state.MeetingsStore.presentationModePageSizeInfo;
  }

  get cardStyles() {
    let cardSyles: any = {};

    if (this.maxContainerWidth && this.maxContainerHeight) {
      let maxWidth = this.maxContainerWidth <= 1000 ? this.maxContainerWidth : 1000;

      let width = maxWidth * 0.7;
      let height = width / 1.4;

      width = 500;
      height = 350;

      cardSyles.width = `${width}px`;
      cardSyles.height = `${height}px`;
    }


    if (this.hasWhiteboard && cardSyles.height) {
      cardSyles.height += 40;
    }

    return cardSyles;
  }

  get isCardLegacy() {
    return this.card.sections[0].type !== 'CARD_DESIGNER';
  }

  getImageStyles(imageUrl: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let width = '100%';
      let height = 'auto';

      const image = new Image();

      image.onload = () => {
        if (image.width <= image.height) {
          width = 'auto';
          height = '100%';
        }

        resolve({
          width,
          height,
        });
      }

      image.onerror = () => {
        resolve({
          width,
          height,
        });
      }

      image.src = imageUrl;
    });
  }

  showFront() {
    this.flipped = false;
  }

  showBack() {
    this.flipped = true;
  }

  toggle() {
    this.pagesLoaded = false;
    this.flipped = !this.flipped;
    this.$emit('onFlipped', this.flipped);
  }

  handleKeydown (e) {
    if(!this.card)
      return
    switch (e.keyCode) {
      case 37:
        this.showFront();
        break;
      case 39:
        this.showBack();
        break;
    }
  }

  async initCardImages() {
    await this.loadImages();
  }

  async loadImages() {
    let frontStyles: any = {};
    let backStyles: any = {};

    if (this.front) {
      let textSection = JSON.parse(this.front.text);
      let imageHash = textSection['background'] || textSection['image'];
      this.imageUrl = this.front.assetsInfo.find(el => el.hash == imageHash)?.url;
      if (this.imageUrl) {
        frontStyles = await this.getImageStyles(this.imageUrl);
      }
    }

    if (this.back) {
      let textBackSection = JSON.parse(this.back.text);
      let imageHashBack = textBackSection['background'] || textBackSection['image'];
      this.imageUrlBack = this.back.assetsInfo.find(el => el.hash == imageHashBack)?.url;
      if (this.imageUrlBack) {
        backStyles = await this.getImageStyles(this.imageUrlBack);
      }
    }

    this.frontImageStyles = frontStyles;
    this.backImageStyles = backStyles;
  }

  updated(value) {
    this.flipped = typeof this.isFlipped === 'boolean' ? this.isFlipped : this.flipped;
    this.loadImages();
    this.loadingPage();
  }

  mounted() {
    this.flipped = this.isFlipped;

    this.loadImages();

    document.addEventListener('keydown', this.handleKeydown, null);
    this.initFlipped();
    
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_CARD_SIDE, this.changeCardSide);
    this.$edEventBus.$emit(COURSE_EVENTS.CARD_CREATED);
  }

  beforeDestroy() {
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_CARD_SIDE, this.changeCardSide);
  }


  destroyed() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  @Watch('flipped')
  onIsFlippedChanged() {
    if (this.flipped) {
      if (!this.$el.querySelector('.back')?.classList.contains('back--focused')) {
        this.$el.querySelector('.back')?.classList.add('back--focused');
      }
    } else {
      if (this.$el.querySelector('.back')?.classList.contains('back--focused')) {
        this.$el.querySelector('.back')?.classList.remove('back--focused');
      }
    }
  }

  @Watch('card', { deep: true, immediate: false })
  cardChanged() {
    this.pagesLoaded = false;
  }

  private changeCardSide(data) {
    this.pagesLoaded = false;
    this.flipped = !data.isFrontSide;
  }

  private initFlipped() {
    if(this.currentCardInfo) {
      this.flipped = !this.currentCardInfo.isFrontSide;
    }
  }

  private loadingPage() {
    const transparencyRef: any = this.$refs.teacherTransparency || this.$refs.studentTransparency;

    let imageElement : any = document.querySelectorAll('.page-image')[0];
    
    if(transparencyRef && this.currentCardInfo && this.currentCardInfo.card && !this.pagesLoaded && imageElement) {
      const cardSide = this.flipped ? 'B' : 'F';
      const sectionToTake = this.flipped ? 1 : 0;
      const index = this.currentCardInfo.card.sections[sectionToTake].id;
      let data = { height: imageElement.offsetHeight, width: imageElement.offsetWidth, sectionType: SECTION_TYPE_CODES.LESSON_CARD,
        page: sectionToTake + 1, side:  cardSide, boardIndex: index};
      transparencyRef.loadingPage(data);
      this.pagesLoaded = true;
      this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);
    }
  }
}
